/// <reference types="react" />

/** @jsxImportSource react */

import VariantImageManager from '../components/VariantImageManager';
import ProductEditForm from '../components/ProductEditForm';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  AlignJustify,
  ChevronDown,
  ChevronUp,
  Copy,
  Download,
  Edit,
  Eye,
  Filter,
  FolderInput,
  Loader2,
  MoveUp,
  Plus,
  Search,
  Settings,
  Trash,
  Upload,
  X,
  PenSquare,
  AlertCircle,
  Tag,
  ArrowUp,
  ExternalLink,
  Sparkles,
  Minus,
  ChevronRight,
  ImageIcon,
  ImagePlus
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import heic2any from 'heic2any';

// Types and interfaces for the application
interface Product {
  id: number;
  nombre: string;
  url_pagina_web?: string;
  costo_promedio?: number;
  precio_local?: number;
  precio_web?: number;
  precio_ml?: number;
  categoria_id: number;
  categoria: string;
  categoria_path?: string;
  updated_at?: string;
  variants: Variant[];
}

interface SupabaseProduct {
  id: number;
  nombre: string;
  url_pagina_web?: string | null;
  costo_promedio?: number | null;
  precio_local?: number | null;
  precio_web?: number | null;
  precio_ml?: number | null;
  categoria_id: number;
  updated_at?: string;
  categorias?: {
    nombre: string;
    path: string;
  };
}

interface EditingProduct {
  id?: number;
  nombre: string;
  url_pagina_web: string | null;
  costo_promedio: string | number;
  precio_local: string | number;
  precio_web: string | number;
  precio_ml: string | number;
  categoria_id: number | null;
  categoria?: string;
  updated_at?: string;
  variants: EditingVariant[];
  applyPriceLocalToVariants?: boolean;
  applyPriceWebToVariants?: boolean;
  applyPriceMlToVariants?: boolean;
  applyDescriptionToVariants?: boolean;
  applyCostToVariants?: boolean;
}

interface ProductImage {
  id: number;
  variante_id: number;
  url: string;
  orden: number;
  principal: boolean;
}

interface Variant {
  id: number;
  sku?: string;
  product_id: number;
  nombre: string;
  descripcion?: string;
  costo_promedio?: number;
  precio_local?: number;
  precio_web?: number;
  precio_ml?: number;
  atributos?: Record<string, string>;
  images?: ProductImage[];
  activo?: boolean;
}

interface EditingVariant {
  id?: number;
  sku: string | null;
  nombre: string;
  descripcion: string | null;
  costo_promedio: string | number;
  precio_local: string | number;
  precio_web: string | number;
  precio_ml: string | number;
  atributos?: Record<string, string> | null;
  images?: ProductImage[];
}

interface Category {
  id: number;
  nombre: string;
  path: string;
  parent_id?: number | null;
}

interface CategoryMenuItemProps {
  category: Category;
  categoriesMap: Map<number, Category[]>;
  level: number;
  selectedCategory: number | null;
  onSelectCategory: (id: number | null) => void;
  openSubmenus: Set<number>;
  toggleSubmenu: (id: number, isOpen: boolean) => void;
}

interface CategoryHoverMenuProps {
  categories: Category[];
  categoriesMap: Map<number, Category[]>;
  onSelectCategory: (id: number | null) => void;
  selectedCategory: number | null;
}

interface CategoryColumnProps {
  categories: Category[];
  categoriesMap: Map<number, Category[]>;
  onSelectCategory: (id: number | null) => void;
  onHover: (id: number | null) => void;
  selectedCategory: number | null;
}

interface CategoryItemProps {
  category: Category;
  hasChildren: boolean;
  isSelected: boolean;
  onSelect: () => void;
  onHover: () => void;
}

// Interface is currently unused, but we're keeping it for future use and type consistency
interface SortConfig {
  field: keyof Product;
  direction: 'asc' | 'desc';
}

interface BulkEditValues {
  categoria_id: number | null;
  precioType: string;
  operation: string;
  percentage: number;
  roundToNearestHundred: boolean;
  aplicar_a_variantes: boolean;
}

interface ProductVariant {
  id?: number;
  product_id?: number;
  sku: string | null;
  nombre: string;
  descripcion: string | null;
  costo_promedio: number;
  precio_local: number;
  precio_web: number;
  precio_ml: number;
  atributos: Record<string, string> | null;
  images?: ProductImage[];
  activo?: boolean;
}

interface ProductImage {
  id: number;
  url: string;
  principal: boolean;
  orden: number;
  producto_variante_id: number;
}

interface CategoryFormValues {
  id?: number;
  nombre: string;
  parent_id: number | null;
}

declare namespace JSX {
  interface IntrinsicElements {
    div: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;
    span: React.DetailedHTMLProps<React.HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>;
    button: React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>;
    input: React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;
    select: React.DetailedHTMLProps<React.SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement>;
    option: React.DetailedHTMLProps<React.OptionHTMLAttributes<HTMLOptionElement>, HTMLOptionElement>;
    form: React.DetailedHTMLProps<React.FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>;
    textarea: React.DetailedHTMLProps<React.TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>;
    table: React.DetailedHTMLProps<React.TableHTMLAttributes<HTMLTableElement>, HTMLTableElement>;
    thead: React.DetailedHTMLProps<React.HTMLAttributes<HTMLTableSectionElement>, HTMLTableSectionElement>;
    tbody: React.DetailedHTMLProps<React.HTMLAttributes<HTMLTableSectionElement>, HTMLTableSectionElement>;
    tr: React.DetailedHTMLProps<React.HTMLAttributes<HTMLTableRowElement>, HTMLTableRowElement>;
    th: React.DetailedHTMLProps<React.ThHTMLAttributes<HTMLTableHeaderCellElement>, HTMLTableHeaderCellElement>;
    td: React.DetailedHTMLProps<React.TdHTMLAttributes<HTMLTableDataCellElement>, HTMLTableDataCellElement>;
    h1: React.DetailedHTMLProps<React.HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>;
    h2: React.DetailedHTMLProps<React.HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>;
    h3: React.DetailedHTMLProps<React.HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>;
    p: React.DetailedHTMLProps<React.HTMLAttributes<HTMLParagraphElement>, HTMLParagraphElement>;
    label: React.DetailedHTMLProps<React.LabelHTMLAttributes<HTMLLabelElement>, HTMLLabelElement>;
    a: React.DetailedHTMLProps<React.AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>;
    [elemName: string]: any;
  }
}

// Update the getCategoryPathDisplay function to use category names instead of IDs
const getCategoryPathDisplay = (path: string | undefined, categories: Category[]): string => {
  if (!path) return 'Sin categoría';
  
  // Remove the leading slash and split by /
  const pathSegments = path.replace(/^\//, '').split('/').filter(Boolean);
  
  // Map each segment (which is a category ID) to its name
  const pathNames = pathSegments.map(idStr => {
    const id = parseInt(idStr, 10);
    const category = categories.find(cat => cat.id === id);
    return category ? category.nombre : idStr;
  });
  
  // Return the formatted path with category names
  return pathNames.join(' > ');
};

export const PriceList = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<{
    field: 'nombre' | 'precio_local' | 'precio_web' | 'precio_ml' | 'costo_promedio' | 'updated_at';
    direction: 'asc' | 'desc';
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedProducts, setExpandedProducts] = useState<Set<number>>(new Set());
  const [editingProduct, setEditingProduct] = useState<EditingProduct | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [saving, setSaving] = useState(false);
  const [generatingDescription, setGeneratingDescription] = useState<'product' | 'variant' | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [showToolsModal, setShowToolsModal] = useState(false);
  const [showBulkEditForm, setShowBulkEditForm] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [showBulkEditConfirmation, setShowBulkEditConfirmation] = useState(false);
  const [bulkEditForm, setBulkEditForm] = useState<BulkEditValues>({
    categoria_id: null,
    precioType: 'local',
    operation: 'increase',
    percentage: 0,
    roundToNearestHundred: true,
    aplicar_a_variantes: false,
  });
  const [generatingDescriptions, setGeneratingDescriptions] = useState(false);
  const [classifyingProducts, setClassifyingProducts] = useState(false);
  const [showGenerateDescriptionsConfirmation, setShowGenerateDescriptionsConfirmation] = useState(false);
  const [showClassifyProductsConfirmation, setShowClassifyProductsConfirmation] = useState(false);
  const [showDeleteProductConfirmation, setShowDeleteProductConfirmation] = useState(false);
  const [showDeleteVariantConfirmation, setShowDeleteVariantConfirmation] = useState<number | null>(null);
  const [editingCategory, setEditingCategory] = useState<CategoryFormValues | null>(null);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<Set<number>>(new Set());
  const [categoryAction, setCategoryAction] = useState<'add' | 'edit' | null>(null);
  const [deletingCategoryId, setDeletingCategoryId] = useState<number | null>(null);

  // New state for AI suggestion
  const [aiSuggestion, setAiSuggestion] = useState<string | null>(null);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [uploadingImages, setUploadingImages] = useState<Record<number, boolean>>({});
  const [imageError, setImageError] = useState<string | null>(null);

  // Fetch AI suggestion when bulk edit form is opened
  useEffect(() => {
    if (showBulkEditForm) {
      fetchAiSuggestion();
    }
  }, [showBulkEditForm]);

  // Function to fetch AI suggestion
  const fetchAiSuggestion = async () => {
    setLoadingSuggestion(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/8dc575a2-54f6-4e1c-8d51-ecfc6609b25b', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id,
        }),
      });

      if (!response.ok) throw new Error('Failed to fetch AI suggestion');

      const data = await response.json();
      setAiSuggestion(data.sugerencia);
    } catch (err) {
      console.error('Error fetching AI suggestion:', err);
      setError(err instanceof Error ? err.message : 'Error fetching AI suggestion');
    } finally {
      setLoadingSuggestion(false);
    }
  };

  // Helper function to calculate profit percentage
  const calculateProfitPercentage = (price: number, cost: number): string => {
    if (cost === 0) return '0%';
    const profitPercentage = ((price - cost) * 100) / cost;
    return `${profitPercentage.toFixed(2)}%`;
  };

  // Fetch categories and subcategories
  useEffect(() => {
    fetchCategoriesAndSubcategories();
  }, []);

  const handleEdit = async (product: Product) => {
    const allVariantsHaveSameValues = product.variants.length > 0 && product.variants.every(variant => 
      variant.precio_local === product.precio_local &&
      variant.precio_web === product.precio_web &&
      variant.precio_ml === product.precio_ml &&
      variant.costo_promedio === product.costo_promedio
    );

    // Fetch variant details including images
    const variantsWithImages = await fetchProductDetails(product.id);
    
    // Map variant images to the product's variants
    const updatedVariants = product.variants.map(variant => {
      const variantWithImages = variantsWithImages.find(v => v.id === variant.id);
      return {
        id: variant.id,
        sku: variant.sku || null,
        nombre: variant.nombre,
        descripcion: variant.descripcion || null,
        costo_promedio: variant.costo_promedio || 0,
        precio_local: variant.precio_local || 0,
        precio_web: variant.precio_web || 0,
        precio_ml: variant.precio_ml || 0,
        atributos: variant.atributos || null,
        images: variantWithImages?.images || []
      };
    });

    setEditingProduct({
      ...product,
      variants: updatedVariants,
      applyPriceLocalToVariants: allVariantsHaveSameValues,
      applyPriceWebToVariants: allVariantsHaveSameValues,
      applyPriceMlToVariants: allVariantsHaveSameValues,
      applyDescriptionToVariants: false,
      applyCostToVariants: allVariantsHaveSameValues,
      categoria_id: product.categoria_id,
    });
    
    fetchCategoriesAndSubcategories();
  };

  const handleAddNewProduct = () => {
    // Create a new empty product
    const newProduct: EditingProduct = {
      id: 0, // This will be assigned by the database
      categoria_id: 0,
      nombre: '',
      url_pagina_web: null,
      costo_promedio: 0,
      precio_local: 0,
      precio_web: 0,
      precio_ml: 0,
      categoria: '',
      updated_at: new Date().toISOString(),
      variants: [
        // Add a default empty variant
        {
          id: Date.now(), // Temporary ID
          sku: null,
          atributos: null,
          nombre: 'Nueva variante',
          descripcion: null,
          costo_promedio: 0,
          precio_local: 0,
          precio_web: 0,
          precio_ml: 0,
          images: []
        }
      ],
      // Always apply prices to variants when creating a new product with a single variant
      applyPriceLocalToVariants: true,
      applyPriceWebToVariants: true,
      applyPriceMlToVariants: true,
      applyDescriptionToVariants: false,
      applyCostToVariants: true
    };

    setEditingProduct(newProduct);
    fetchCategoriesAndSubcategories();
  };

  const fetchCategoriesAndSubcategories = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');

      const { data: categoriesData, error: categoriesError } = await supabase
        .schema(profile.company_id)
        .from('categorias')
        .select('id, nombre, path')
        .order('nombre');

      if (categoriesError) throw categoriesError;

      setCategories(categoriesData || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError(err instanceof Error ? err.message : 'Error loading categories');
    }
  };

  const handleSave = async () => {
    if (!editingProduct) return;
    
    if (!editingProduct.nombre.trim()) {
      setError('El nombre del producto es obligatorio');
      return;
    }
    
    if (!editingProduct.categoria_id) {
      setError('Debe seleccionar una categoría');
      return;
    }
    
    setSaving(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');

      // Check if this is a new product (id = 0) or an existing one
      if (editingProduct.id === 0) {
        // Insert new product
        const { data: newProduct, error: productError } = await supabase
          .schema(profile.company_id)
          .from('productos')
          .insert({
            nombre: editingProduct.nombre,
            url_pagina_web: editingProduct.url_pagina_web,
            costo_promedio: editingProduct.costo_promedio,
            precio_local: editingProduct.precio_local,
            precio_web: editingProduct.precio_web,
            precio_ml: editingProduct.precio_ml,
            categoria_id: editingProduct.categoria_id,
            updated_at: new Date().toISOString(),
          })
          .select()
          .single();

        if (productError) throw productError;
        if (!newProduct) throw new Error('Failed to create product');

        // Insert variants sequentially, ensuring the product exists first
        if (editingProduct.variants.length > 0) {
          const variantInsertErrors = [];
          
          // Process variants one by one
          for (const variant of editingProduct.variants) {
            const { error: variantError } = await supabase
              .schema(profile.company_id)
              .from('variantes')
              .insert({
                product_id: newProduct.id,
                nombre: variant.nombre,
                descripcion: variant.descripcion,
                sku: variant.sku,
                costo_promedio: editingProduct.variants.length === 1 || editingProduct.applyCostToVariants 
                  ? editingProduct.costo_promedio 
                  : variant.costo_promedio,
                precio_local: editingProduct.variants.length === 1 || editingProduct.applyPriceLocalToVariants 
                  ? editingProduct.precio_local 
                  : variant.precio_local,
                precio_web: editingProduct.variants.length === 1 || editingProduct.applyPriceWebToVariants 
                  ? editingProduct.precio_web 
                  : variant.precio_web,
                precio_ml: editingProduct.variants.length === 1 || editingProduct.applyPriceMlToVariants 
                  ? editingProduct.precio_ml 
                  : variant.precio_ml
              });
              
            if (variantError) {
              variantInsertErrors.push(variantError);
            }
          }
          
          if (variantInsertErrors.length > 0) {
            console.error('Errors inserting variants:', variantInsertErrors);
            throw new Error(`Failed to insert ${variantInsertErrors.length} variants`);
          }
        }

        // Fetch the newly created product with its variants to add to the products list
        const { data: productWithVariants, error: fetchError } = await supabase
          .schema(profile.company_id)
          .from('productos')
          .select(`
            *,
            variants:variantes(*),
            categorias(id, nombre, path)
          `)
          .eq('id', newProduct.id)
          .single();

        if (fetchError) throw fetchError;

        // Transform the fetched data to include category information
        const formattedProduct = {
          ...productWithVariants,
          categoria: productWithVariants.categorias?.nombre || 'Sin categoría',
          categoria_path: productWithVariants.categorias?.path || '',
        };

        // Add the new product to the products list
        setProducts(current => [...current, formattedProduct]);
      } else {
        // Update existing product
        const { error: productError } = await supabase
          .schema(profile.company_id)
          .from('productos')
          .update({
            nombre: editingProduct.nombre,
            url_pagina_web: editingProduct.url_pagina_web,
            costo_promedio: editingProduct.costo_promedio,
            precio_local: editingProduct.precio_local,
            precio_web: editingProduct.precio_web,
            precio_ml: editingProduct.precio_ml,
            categoria_id: editingProduct.categoria_id,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingProduct.id);

        if (productError) throw productError;

        if (editingProduct.variants.length > 0) {
          const updates = editingProduct.variants.map(variant => {
            // Check if this is a new variant (temporary ID) or an existing one
            if (variant.id > 1000000000) { // Assuming Date.now() IDs are very large numbers
              // Insert new variant
              return supabase
                .schema(profile.company_id)
                .from('variantes')
                .insert({
                  product_id: editingProduct.id,
                  nombre: variant.nombre,
                  descripcion: variant.descripcion,
                  sku: variant.sku,
                  costo_promedio: editingProduct.variants.length === 1 || editingProduct.applyCostToVariants 
                    ? editingProduct.costo_promedio 
                    : variant.costo_promedio,
                  precio_local: editingProduct.variants.length === 1 || editingProduct.applyPriceLocalToVariants 
                    ? editingProduct.precio_local 
                    : variant.precio_local,
                  precio_web: editingProduct.variants.length === 1 || editingProduct.applyPriceWebToVariants 
                    ? editingProduct.precio_web 
                    : variant.precio_web,
                  precio_ml: editingProduct.variants.length === 1 || editingProduct.applyPriceMlToVariants 
                    ? editingProduct.precio_ml 
                    : variant.precio_ml
                });
            } else {
              // Update existing variant
              const update: any = {
                nombre: variant.nombre,
                descripcion: variant.descripcion,
                sku: variant.sku,
                atributos: variant.atributos
              };
              
              // Only add numeric properties that should be updated
              if (editingProduct.variants.length === 1 || editingProduct.applyCostToVariants) {
                update.costo_promedio = editingProduct.costo_promedio;
              }
              if (editingProduct.variants.length === 1 || editingProduct.applyPriceLocalToVariants) {
                update.precio_local = editingProduct.precio_local;
              }
              if (editingProduct.variants.length === 1 || editingProduct.applyPriceWebToVariants) {
                update.precio_web = editingProduct.precio_web;
              }
              if (editingProduct.variants.length === 1 || editingProduct.applyPriceMlToVariants) {
                update.precio_ml = editingProduct.precio_ml;
              }
              return supabase
                .schema(profile.company_id)
                .from('variantes')
                .update(update)
                .eq('id', variant.id);
            }
          });

          // Execute all updates sequentially
          for (const update of updates) {
            await update;
          }
        }

        // Fetch the updated product with its variants
        const { data: updatedProductWithVariants, error: fetchError } = await supabase
          .schema(profile.company_id)
          .from('productos')
          .select(`
            *,
            variants:variantes(*),
            categorias(id, nombre, path)
          `)
          .eq('id', editingProduct.id)
          .single();

        if (fetchError) throw fetchError;

        // Transform the fetched data to include category information
        const formattedProduct = {
          ...updatedProductWithVariants,
          categoria: updatedProductWithVariants.categorias?.nombre || 'Sin categoría',
          categoria_path: updatedProductWithVariants.categorias?.path || '',
        };

        // Update the products list
        setProducts(current =>
          current.map(p => p.id === editingProduct.id ? formattedProduct : p)
        );
      }

      setEditingProduct(null);
    } catch (err) {
      console.error('Error saving product:', err);
      setError(err instanceof Error ? err.message : 'Error saving product');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateDescription = async (type: 'product' | 'variant', variantId?: number) => {
    if (!editingProduct) return;
    setGeneratingDescription(type);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/64060904-237a-4beb-aa90-42fe6414c262', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id,
          product_name: type === 'product' ? editingProduct.nombre : 
            editingProduct.variants.find(v => v.id === variantId)?.nombre,
          description_type: type,
        }),
      });

      if (!response.ok) throw new Error('Failed to generate description');

      const data = await response.json();
      
      if (type === 'product') {
        setEditingProduct(prev => prev ? {
          ...prev,
          descripcion_breve: data.description
        } : null);
      } else if (variantId) {
        setEditingProduct(prev => prev ? {
          ...prev,
          variants: prev.variants.map(v =>
            v.id === variantId ? { ...v, descripcion: data.description } : v
          )
        } : null);
      }
    } catch (err) {
      console.error('Error generating description:', err);
      setError(err instanceof Error ? err.message : 'Error generating description');
    } finally {
      setGeneratingDescription(null);
    }
  };

  const toggleProduct = (productId: number) => {
    setExpandedProducts(current => {
      const newSet = new Set(current);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const fetchProducts = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');

      const { data: productsData, error: productsError } = await supabase
        .schema(profile.company_id)
        .from('productos')
        .select(`
          *,
          variants:variantes(*),
          categorias(id, nombre, path)
        `)
        .eq('activo', true)
        .order('id');

      if (productsError) throw productsError;

      // Transform the data to format expected by the app
      const formattedProducts: Product[] = (productsData || []).map((product: SupabaseProduct & { variants: Variant[], categorias: { nombre: string, path: string } | null }) => ({
        id: product.id,
        nombre: product.nombre,
        url_pagina_web: product.url_pagina_web || undefined,
        costo_promedio: product.costo_promedio || 0,
        precio_local: product.precio_local || 0,
        precio_web: product.precio_web || 0,
        precio_ml: product.precio_ml || 0,
        categoria_id: product.categoria_id,
        categoria: product.categorias?.nombre || 'Sin categoría',
        categoria_path: product.categorias?.path || '',
        updated_at: product.updated_at,
        variants: (product.variants || []).filter(variant => Boolean(variant.activo !== false)),
      }));

      setProducts(formattedProducts);
      setFilteredProducts(formattedProducts);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err instanceof Error ? err.message : 'Error loading products');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (!products.length) return;
    
    let result = [...products];
    
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter((product: Product) => 
        product.nombre.toLowerCase().includes(searchLower) || 
        product.variants.some((variant: Variant) => 
          variant.nombre.toLowerCase().includes(searchLower) || 
          (variant.descripcion && variant.descripcion.toLowerCase().includes(searchLower)) ||
          (variant.sku && variant.sku.toLowerCase().includes(searchLower))
        )
      );
    }
    
    if (selectedCategory) {
      // Get all subcategories of the selected category
      const selectedCategoryPath = categories.find((c: Category) => c.id === selectedCategory)?.path || '';
      
      // Filter products to include the selected category and all its subcategories
      result = result.filter((product: Product) => {
        const categoryPath = categories.find((c: Category) => c.id === product.categoria_id)?.path || '';
        return categoryPath.startsWith(selectedCategoryPath);
      });
    }
    
    if (sortBy) {
      result.sort((a: Product, b: Product) => {
        const aValue = a[sortBy.field];
        const bValue = b[sortBy.field];
        
        // Handle string comparison
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortBy.direction === 'asc' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        }
        
        // Handle number or date comparison
        return sortBy.direction === 'asc' ? Number(aValue) - Number(bValue) : Number(bValue) - Number(aValue);
      });
    }
    
    setFilteredProducts(result);
  }, [products, searchTerm, sortBy, selectedCategory, categories]);

  // Function to handle bulk edit form inputs - used in various form elements
  const handleBulkEditFormChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setBulkEditForm((prev: Record<string, any>) => ({
      ...prev,
      [name]: type === 'checkbox' 
        ? (e.target as HTMLInputElement).checked 
        : name === 'percentage' || name === 'categoria_id' 
          ? parseInt(value) 
          : value
    }));
  }, []);

  const handleBulkEditSubmit = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');

      const { categoria_id, precioType, operation, percentage, roundToNearestHundred } = bulkEditForm;

      // Build the query dynamically based on whether sub_categoria_id is null
      let query = supabase
        .schema(profile.company_id)
        .from('productos')
        .select('*')
        .eq('categoria_id', categoria_id)
        .eq('activo', true);

      const { data: productsData, error: productsError } = await query;

      if (productsError) throw productsError;

      // Apply price adjustments to products
      const productUpdates = productsData.map(product => {
        const newPrecioLocal = operation === 'increase' 
          ? product.precio_local * (1 + percentage / 100)
          : product.precio_local * (1 - percentage / 100);
        
        const newPrecioWeb = operation === 'increase' 
          ? product.precio_web * (1 + percentage / 100)
          : product.precio_web * (1 - percentage / 100);

        const newPrecioMl = operation === 'increase' 
          ? product.precio_ml * (1 + percentage / 100)
          : product.precio_ml * (1 - percentage / 100);

        // Round prices if the toggle is enabled
        const roundedPrecioLocal = roundToNearestHundred ? Math.round(newPrecioLocal / 100) * 100 : newPrecioLocal;
        const roundedPrecioWeb = roundToNearestHundred ? Math.round(newPrecioWeb / 100) * 100 : newPrecioWeb;
        const roundedPrecioMl = roundToNearestHundred ? Math.round(newPrecioMl / 100) * 100 : newPrecioMl;

        return supabase
          .schema(profile.company_id)
          .from('productos')
          .update({
            precio_local: precioType === 'local' || precioType === 'both' ? roundedPrecioLocal : product.precio_local,
            precio_web: precioType === 'web' || precioType === 'both' ? roundedPrecioWeb : product.precio_web,
            precio_ml: precioType === 'ml' || precioType === 'both' ? roundedPrecioMl : product.precio_ml,
            updated_at: new Date().toISOString(),
          })
          .eq('id', product.id);
      });

      // Fetch variants for the selected products
      const { data: variantsData, error: variantsError } = await supabase
        .schema(profile.company_id)
        .from('variantes')
        .select('*')
        .in('product_id', productsData.map(p => p.id))
        .eq('activo', true);

      if (variantsError) throw variantsError;

      // Apply price adjustments to variants
      const variantUpdates = variantsData.map(variant => {
        const product = productsData.find(p => p.id === variant.product_id);
        if (!product) return null;

        const newPrecioLocal = operation === 'increase' 
          ? variant.precio_local * (1 + percentage / 100)
          : variant.precio_local * (1 - percentage / 100);
        
        const newPrecioWeb = operation === 'increase' 
          ? variant.precio_web * (1 + percentage / 100)
          : variant.precio_web * (1 - percentage / 100);

        const newPrecioMl = operation === 'increase' 
          ? variant.precio_ml * (1 + percentage / 100)
          : variant.precio_ml * (1 - percentage / 100);

        // Round prices if the toggle is enabled
        const roundedPrecioLocal = roundToNearestHundred ? Math.round(newPrecioLocal / 100) * 100 : newPrecioLocal;
        const roundedPrecioWeb = roundToNearestHundred ? Math.round(newPrecioWeb / 100) * 100 : newPrecioWeb;
        const roundedPrecioMl = roundToNearestHundred ? Math.round(newPrecioMl / 100) * 100 : newPrecioMl;

        return supabase
          .schema(profile.company_id)
          .from('variantes')
          .update({
            precio_local: precioType === 'local' || precioType === 'both' ? roundedPrecioLocal : variant.precio_local,
            precio_web: precioType === 'web' || precioType === 'both' ? roundedPrecioWeb : variant.precio_web,
            precio_ml: precioType === 'ml' || precioType === 'both' ? roundedPrecioMl : variant.precio_ml,
          })
          .eq('id', variant.id);
      });

      // Execute all updates sequentially
      for (const update of [...productUpdates, ...variantUpdates]) {
        await update;
      }

      // Refresh the product list
      fetchProducts();
      setShowBulkEditForm(false);
      setShowBulkEditConfirmation(false); // Close confirmation card
    } catch (err) {
      console.error('Error during bulk edit:', err);
      setError(err instanceof Error ? err.message : 'Error during bulk edit');
    }
  };

  const handleGenerateAllDescriptions = async () => {
    setGeneratingDescriptions(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/10e2cb66-5a97-4702-aa05-4e042d51237c', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id,
          modo: 'describir_todo',
        }),
      });

      if (!response.ok) throw new Error('Failed to generate descriptions');

      const data = await response.json();
      if (data.status !== 'success') throw new Error('Error generating descriptions');

      // Refresh the product list
      fetchProducts();
    } catch (err) {
      console.error('Error generating descriptions:', err);
      setError(err instanceof Error ? err.message : 'Error generating descriptions');
    } finally {
      setGeneratingDescriptions(false);
      setShowGenerateDescriptionsConfirmation(false);
    }
  };

  const handleClassifyProducts = async () => {
    setClassifyingProducts(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/aceebea0-3ae3-4ad6-a130-33a8da65e0bc', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id,
        }),
      });

      if (!response.ok) throw new Error('Failed to classify products');

      const data = await response.json();
      if (data.status !== 'success') throw new Error('Error classifying products');

      // Refresh the product list
      fetchProducts();
    } catch (err) {
      console.error('Error classifying products:', err);
      setError(err instanceof Error ? err.message : 'Error classifying products');
    } finally {
      setClassifyingProducts(false);
      setShowClassifyProductsConfirmation(false);
    }
  };

  const handleDeleteProduct = async () => {
    if (!editingProduct || !editingProduct.id) return;
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');

      // Soft delete all variants by setting activo=false
      const { error: variantsError } = await supabase
        .schema(profile.company_id)
        .from('variantes')
        .update({ activo: false })
        .eq('product_id', editingProduct.id);
      
      if (variantsError) {
        console.error('Error deactivating variants:', variantsError);
        throw new Error(`Failed to deactivate variants: ${variantsError.message}`);
      }

      // Soft delete the product by setting activo=false
      const { error: productError } = await supabase
        .schema(profile.company_id)
        .from('productos')
        .update({ activo: false })
        .eq('id', editingProduct.id);
      
      if (productError) {
        console.error('Error deactivating product:', productError);
        throw new Error(`Failed to deactivate product: ${productError.message}`);
      }

      // Update the products list by removing the deactivated product
      setProducts(current => current.filter(p => p.id !== editingProduct.id));
      setEditingProduct(null);
      setShowDeleteProductConfirmation(false);
    } catch (err) {
      console.error('Error deactivating product:', err);
      setError(err instanceof Error ? err.message : 'Error deactivating product');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVariant = async (variantId: number) => {
    if (!editingProduct) return;
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');

      // Soft delete the variant by setting activo=false
      const { error: variantError } = await supabase
        .schema(profile.company_id)
        .from('variantes')
        .update({ activo: false })
        .eq('id', variantId);
        
      if (variantError) {
        console.error('Error deactivating variant:', variantError);
        throw new Error(`Failed to deactivate variant: ${variantError.message}`);
      }

      // Update the editing product state to filter out the deactivated variant
      setEditingProduct(prev => {
        if (!prev) return null;
        
        return {
          ...prev,
          variants: prev.variants.filter(v => v.id !== variantId)
        };
      });

      setShowDeleteVariantConfirmation(null);
    } catch (err) {
      console.error('Error deactivating variant:', err);
      setError(err instanceof Error ? err.message : 'Error deactivating variant');
    } finally {
      setSaving(false);
    }
  };

  const handleAddVariant = () => {
    if (!editingProduct) return;
    const newVariant: ProductVariant = {
      id: Date.now(), // Temporary ID
      sku: null,
      atributos: null,
      nombre: 'Nueva variante',
      descripcion: null,
      costo_promedio: editingProduct.costo_promedio,
      precio_local: editingProduct.precio_local,
      precio_web: editingProduct.precio_web,
      precio_ml: editingProduct.precio_ml,
      images: []
    };
    setEditingProduct(prev => prev ? {
      ...prev,
      variants: [...prev.variants, newVariant]
    } : null);
  };

  // Compute a categoriesMap for use with the CategoryHoverMenu
  const categoriesMap = useMemo(() => {
    const map = new Map<number, Category[]>();
    
    // First pass: Process all categories and add parent_id based on path
    const processedCategories = categories.map(category => {
      const pathSegments = category.path.split('/').filter(Boolean);
      
      // Find parent category based on path
      let parent_id: number | null = null;
      if (pathSegments.length > 1) {
        // Find the parent category by matching paths
        const parentPath = '/' + pathSegments.slice(0, -1).join('/');
        const parentCategory = categories.find(c => c.path === parentPath);
        if (parentCategory) {
          parent_id = parentCategory.id;
        }
      }
      
      return {
        ...category,
        parent_id
      };
    });
    
    // Second pass: Group categories by their parent_id
    processedCategories.forEach((category) => {
      const parentId = category.parent_id || 0; // Use 0 for root categories
      if (!map.has(parentId)) {
        map.set(parentId, []);
      }
      map.get(parentId)!.push(category);
    });
    
    return map;
  }, [categories]);

  // Get root level categories
  const rootCategories = useMemo(() => {
    return categories.filter(category => {
      const segments = category.path.split('/').filter(Boolean);
      return segments.length === 1;
    });
  }, [categories]);

  // State to keep track of which category submenus are open
  const [openSubmenus, setOpenSubmenus] = useState<Set<number>>(new Set());

  // Toggle submenu open/closed
  const toggleSubmenu = (categoryId: number, isOpen: boolean) => {
    setOpenSubmenus(prev => {
      const next = new Set(prev);
      if (isOpen) {
        next.add(categoryId);
      } else {
        next.delete(categoryId);
      }
      return next;
    });
  };

  // State for bulk edit category selector
  const [showBulkEditCategoryMenu, setShowBulkEditCategoryMenu] = useState<boolean>(false);

  const handleToggleCategoryExpansion = (categoryId: number) => {
    setExpandedCategoryIds(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(categoryId)) {
        newExpanded.delete(categoryId);
      } else {
        newExpanded.add(categoryId);
      }
      return newExpanded;
    });
  };

  const handleAddCategory = (parentId: number | null = null) => {
    setEditingCategory({
      nombre: '',
      parent_id: parentId
    });
    setCategoryAction('add');
    // Scroll to top of the category manager modal instead of the window
    setTimeout(() => {
      if (categoryModalRef.current) {
        categoryModalRef.current.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }, 10);
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory({
      id: category.id,
      nombre: category.nombre,
      parent_id: getParentIdFromPath(category.path)
    });
    setCategoryAction('edit');
  };

  const getParentIdFromPath = (path: string): number | null => {
    const parts = path.split('/').filter(Boolean);
    if (parts.length <= 1) return null;
    return parseInt(parts[parts.length - 2], 10);
  };

  const handleSaveCategory = async () => {
    if (!editingCategory) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');

      // Construct the path
      let path = '';
      if (editingCategory.parent_id) {
        // Find the parent category to get its path
        const parentCategory = categories.find(cat => cat.id === editingCategory.parent_id);
        if (parentCategory) {
          path = parentCategory.path;
        }
      }

      if (categoryAction === 'add') {
        // Insert new category
        const { data: newCategory, error } = await supabase
          .schema(profile.company_id)
          .from('categorias')
          .insert({
            nombre: editingCategory.nombre,
            // The path will be updated after we get the ID
            path: ''
          })
          .select('id')
          .single();

        if (error) throw error;
        
        if (newCategory) {
          // Update the path with the new ID
          const newPath = path ? `${path}/${newCategory.id}` : `/${newCategory.id}`;
          
          const { error: updateError } = await supabase
            .schema(profile.company_id)
            .from('categorias')
            .update({ path: newPath })
            .eq('id', newCategory.id);
            
          if (updateError) throw updateError;
        }
      } else if (categoryAction === 'edit' && editingCategory.id) {
        // Update existing category
        const { error } = await supabase
          .schema(profile.company_id)
          .from('categorias')
          .update({
            nombre: editingCategory.nombre
          })
          .eq('id', editingCategory.id);

        if (error) throw error;
      }

      // Refresh categories
      await fetchCategoriesAndSubcategories();
      setEditingCategory(null);
      setCategoryAction(null);
    } catch (err) {
      console.error('Error saving category:', err);
      setError(err instanceof Error ? err.message : 'Error saving category');
    }
  };

  const handleDeleteCategory = async (categoryId: number) => {
    try {
      setDeletingCategoryId(categoryId);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');

      // First check if there are any products using this category
      const { count: productsCount, error: countError } = await supabase
        .schema(profile.company_id)
        .from('productos')
        .select('id', { count: 'exact', head: true })
        .eq('categoria_id', categoryId);

      if (countError) throw countError;

      // Check if category has children
      const category = categories.find(cat => cat.id === categoryId);
      if (!category) throw new Error('Category not found');
      
      const hasChildren = categories.some(cat => 
        cat.path.startsWith(category.path) && cat.id !== categoryId
      );

      if (productsCount && productsCount > 0) {
        setError(`No se puede eliminar la categoría porque hay ${productsCount} productos asociados a ella.`);
        return;
      }

      if (hasChildren) {
        setError('No se puede eliminar la categoría porque tiene subcategorías.');
        return;
      }

      // Delete the category
      const { error } = await supabase
        .schema(profile.company_id)
        .from('categorias')
        .delete()
        .eq('id', categoryId);

      if (error) throw error;

      // Refresh categories
      await fetchCategoriesAndSubcategories();
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(err instanceof Error ? err.message : 'Error deleting category');
    } finally {
      setDeletingCategoryId(null);
    }
  };

  const handleCancelCategoryEdit = () => {
    setEditingCategory(null);
    setCategoryAction(null);
    setError(null); // Clear any error messages
  };

  // Add ref for category modal content container
  const categoryModalRef = useRef<HTMLDivElement>(null);

  // Fetch product details including variants and their images
  const fetchProductDetails = async (productId: number) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');
      
      // Fetch variants for the product
      const { data: variantsData, error: variantsError } = await supabase
        .schema(profile.company_id)
        .from('variantes')
        .select('*')
        .eq('product_id', productId)
        .eq('activo', true);
      
      if (variantsError) throw variantsError;
      
      // Fetch images for each variant
      for (const variant of variantsData || []) {
        const { data: imagesData, error: imagesError } = await supabase
          .schema(profile.company_id)
          .from('productos_imagenes')
          .select('*')
          .eq('variante_id', variant.id)
          .order('orden', { ascending: true });
        
        if (imagesError) throw imagesError;
        
        variant.images = imagesData || [];
      }
      
      return variantsData;
    } catch (err) {
      console.error('Error fetching product details:', err);
      setError(err instanceof Error ? err.message : 'Error fetching product details');
      return [];
    }
  };

  const handleImageUpload = async (variantId: number, file: File) => {
    try {
      setUploadingImages(prev => ({ ...prev, [variantId]: true }));
      setImageError(null);
      
      // Validate file size (limit: 100 MB)
      const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
      if (file.size > MAX_FILE_SIZE) {
        setImageError('El archivo supera el límite de 100 MB. Reducí el tamaño e intentá de nuevo.');
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');

      // Process HEIC files before uploading
      let processedFile = file;
      let fileName = file.name;
      
      if (
        file.type === 'image/heic' || 
        file.type === 'image/heif' || 
        file.name.toLowerCase().endsWith('.heic') || 
        file.name.toLowerCase().endsWith('.heif')
      ) {
        console.log('Converting HEIC image to JPEG before uploading...');
        try {
          // Convert HEIC to JPEG using heic2any
          const convertedBlob = await heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: 0.8
          });
          
          // Create a new File object from the converted blob
          fileName = file.name.replace(/\.heic$/i, '.jpg').replace(/\.heif$/i, '.jpg');
          processedFile = new File([convertedBlob as Blob], fileName, { type: 'image/jpeg' });
          
          console.log('HEIC image successfully converted to JPEG for upload');
        } catch (conversionError) {
          console.error('Error converting HEIC image:', conversionError);
          throw new Error('No pudimos convertir la imagen HEIC. Probá con una imagen en formato png o jpg');
        }
      }

      // Create filenames for both full and optimized versions
      const timestamp = new Date().getTime();
      const fileBaseName = `${timestamp}_${fileName}`;
      
      // Upload full resolution image to shared bucket 'productos'
      const idCompany = (profile as any).id_company ?? profile.company_id;
      const fullFilePath = `${idCompany}/${variantId}/full/${fileBaseName}`;
      
      const { error: uploadError } = await supabase
        .storage
        .from('productos')
        .upload(fullFilePath, processedFile);

      if (uploadError) throw uploadError;
      
      // Also create and upload optimized version to 'opt' folder in shared bucket
      const optFilePath = `${idCompany}/${variantId}/opt/${fileBaseName}`;
      
      // Create a canvas to resize the image
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }
      
      // Set up a promise to handle the image loading
      const resizePromise = new Promise<Blob>((resolve, reject) => {
        img.onload = () => {
          // Target dimensions for optimized version - 300x300 pixels
          const targetWidth = 300;
          const targetHeight = 300;
          
          // Calculate dimensions to maintain aspect ratio
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > targetWidth) {
              height = Math.round(height * (targetWidth / width));
              width = targetWidth;
            }
          } else {
            if (height > targetHeight) {
              width = Math.round(width * (targetHeight / height));
              height = targetHeight;
            }
          }
          
          // Set canvas dimensions and draw resized image
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to JPEG blob with 80% quality
          canvas.toBlob(
            blob => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Failed to create image blob'));
              }
            },
            'image/jpeg',
            0.8
          );
        };
        
        img.onerror = () => {
          reject(new Error('Failed to load image for optimization'));
        };
        
        // Create a URL from the processed file for the Image to load
        img.src = URL.createObjectURL(processedFile);
      });
      
      try {
        // Wait for the image to be resized
        const optimizedBlob = await resizePromise;
        
        // Upload the optimized version
        const { error: optUploadError } = await supabase
          .storage
          .from('productos')
          .upload(optFilePath, optimizedBlob);
        
        if (optUploadError) {
          console.error('Error uploading optimized image:', optUploadError);
          // Continue anyway as we at least have the full version
        }
        
        // Clean up the object URL
        URL.revokeObjectURL(img.src);
      } catch (resizeError) {
        console.error('Error creating optimized image:', resizeError);
        // Continue anyway as we at least have the full version
      }

      // Find the highest order value for this variant
      const { data: existingImages } = await supabase
        .schema(profile.company_id)
        .from('productos_imagenes')
        .select('orden')
        .eq('variante_id', variantId)
        .order('orden', { ascending: false })
        .limit(1);
      
      const nextOrder = existingImages && existingImages.length > 0 ? existingImages[0].orden + 1 : 0;
      
      // Set as principal if it's the first image
      const isPrincipal = existingImages?.length === 0;
      
      // Insert image record with just the filename
      const { data: imageRecord, error: insertError } = await supabase
        .schema(profile.company_id)
        .from('productos_imagenes')
        .insert({
          variante_id: variantId,
          url: fileBaseName, // Store only the filename, not the full URL
          orden: nextOrder,
          principal: isPrincipal
        })
        .select()
        .single();
      
      if (insertError) throw insertError;
      
      // Update the UI
      setEditingProduct(prev => {
        if (!prev) return null;
        
        return {
          ...prev,
          variants: prev.variants.map(v =>
            v.id === variantId
              ? { 
                  ...v, 
                  images: [...(v.images || []), imageRecord]
                }
              : v
          )
        };
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      setImageError(error instanceof Error ? error.message : 'Error al subir la imagen. Por favor, inténtalo de nuevo.');
    } finally {
      setUploadingImages(prev => ({ ...prev, [variantId]: false }));
    }
  };

  const handleDeleteImage = async (variantId: number, imageId: number) => {
    try {
      // Get the current user and profile
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');
      
      // Get image info before deleting
      const { data: imageData, error: imageError } = await supabase
        .schema(profile.company_id)
        .from('productos_imagenes')
        .select('*')
        .eq('id', imageId)
        .single();
      
      if (imageError) throw imageError;
      
      // Delete image from storage if possible
      if (imageData?.url) {
        const urlParts = imageData.url.split('/');
        const fileName = urlParts[urlParts.length - 1];
        const idCompany = (profile as any).id_company ?? profile.company_id;
        const fullPath = `${idCompany}/${variantId}/full/${fileName}`;
        const optPath = `${idCompany}/${variantId}/opt/${fileName}`;
        
        await supabase
          .storage
          .from('productos')
          .remove([fullPath, optPath]);
      }
      
      // Delete record from productos_imagenes
      const { error: deleteError } = await supabase
        .schema(profile.company_id)
        .from('productos_imagenes')
        .delete()
        .eq('id', imageId);

      if (deleteError) throw deleteError;

      // Check if this was the principal image and update a new one if needed
      if (imageData?.principal) {
        // Find another image to set as principal
        const { data: otherImages } = await supabase
          .schema(profile.company_id)
          .from('productos_imagenes')
          .select('id')
          .eq('variante_id', variantId)
          .limit(1);
          
        if (otherImages && otherImages.length > 0) {
          // Set the first available image as principal
          await supabase
            .schema(profile.company_id)
            .from('productos_imagenes')
            .update({ principal: true })
            .eq('id', otherImages[0].id);
        }
      }
      
      // Update the UI
      setEditingProduct(prev => {
        if (!prev) return null;
        
        return {
          ...prev,
          variants: prev.variants.map(v =>
            v.id === variantId
              ? { 
                  ...v, 
                  images: (v.images || []).filter(img => img.id !== imageId)
                }
              : v
          )
        };
      });
    } catch (err) {
      console.error('Error deleting image:', err);
      setImageError('Error al eliminar la imagen. Por favor, inténtalo de nuevo.');
    }
  };

  const handleSetPrincipalImage = async (variantId: number, imageId: number) => {
    try {
      // Get the current user and profile
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');
      
      // First, unset all current principal images for this variant
      await supabase
        .schema(profile.company_id)
        .from('productos_imagenes')
        .update({ principal: false })
        .eq('variante_id', variantId);
      
      // Then set the selected image as principal
      await supabase
        .schema(profile.company_id)
        .from('productos_imagenes')
        .update({ principal: true })
        .eq('id', imageId);
      
      // Update the UI
      setEditingProduct(prev => {
        if (!prev) return null;
        
        return {
          ...prev,
          variants: prev.variants.map(v =>
            v.id === variantId
              ? { 
                  ...v, 
                  images: v.images?.map(img => ({
                    ...img,
                    principal: img.id === imageId
                  })) || []
                }
              : v
          )
        };
      });
    } catch (err) {
      console.error('Error setting principal image:', err);
      setImageError('Error al establecer la imagen principal. Por favor, inténtalo de nuevo.');
    }
  };

  const handleReorderImages = async (variantId: number, imageId: number, direction: 'up' | 'down') => {
    try {
      // Get the current user and profile
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');
      
      const variant = editingProduct?.variants.find(v => v.id === variantId);
      if (!variant?.images || variant.images.length <= 1) return;
      
      const currentImages = [...variant.images];
      const currentIndex = currentImages.findIndex(img => img.id === imageId);
      
      if (currentIndex === -1 || 
          (direction === 'up' && currentIndex === 0) || 
          (direction === 'down' && currentIndex === currentImages.length - 1)) {
        return;
      }
      
      // Calculate new index for the image
      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      
      // Swap the images
      const temp = currentImages[currentIndex];
      currentImages[currentIndex] = currentImages[newIndex];
      currentImages[newIndex] = temp;
      
      // Update orders in database
      await Promise.all(currentImages.map((img, index) => 
        supabase
          .schema(profile.company_id)
          .from('productos_imagenes')
          .update({ orden: index })
          .eq('id', img.id)
      ));
      
      // Update the UI
      setEditingProduct(prev => {
        if (!prev) return null;
        
        return {
          ...prev,
          variants: prev.variants.map(v =>
            v.id === variantId
              ? { ...v, images: currentImages }
              : v
          )
        };
      });
    } catch (err) {
      console.error('Error reordering images:', err);
      setImageError('Error al reordenar las imágenes. Por favor, inténtalo de nuevo.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="flex items-center gap-2 text-red-600 bg-red-50 p-4 rounded-lg border border-red-200">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8 flex items-center gap-3">
        <Tag className="w-8 h-8 text-gray-700" />
        <h1 className="text-2xl font-semibold text-gray-900">Lista de productos</h1>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Buscar productos por nombre..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          </div>
          
          <div className="relative w-64">
            <button
              onClick={() => {
                const menu = document.getElementById('category-menu');
                if (menu) menu.classList.toggle('hidden');
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-between"
            >
              <span>{selectedCategory ? categories.find(c => c.id === selectedCategory)?.nombre || 'Categoría' : 'Todas las categorías'}</span>
              <ChevronDown className="w-4 h-4" />
            </button>
            
            <div
              id="category-menu"
              className="hidden absolute left-0 mt-2 z-20"
            >
              <CategoryHoverMenu
                categories={categories}
                categoriesMap={categoriesMap}
                onSelectCategory={(id: number | null) => {
                  setSelectedCategory(id === null ? null : id);
                  document.getElementById('category-menu')?.classList.add('hidden');
                }}
                selectedCategory={selectedCategory}
              />
            </div>
          </div>
          
          <div className="relative">
            <button
              onClick={() => {
                const menu = document.getElementById('sort-menu');
                if (menu) menu.classList.toggle('hidden');
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <ArrowUp className="w-4 h-4" />
              <span>Ordenar por</span>
              <ChevronDown className="w-4 h-4" />
            </button>
            
            <div
              id="sort-menu"
              className="hidden absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10"
            >
              {[
                { field: 'nombre', label: 'Nombre' },
                { field: 'precio_local', label: 'Precio local' },
                { field: 'precio_web', label: 'Precio web' },
                { field: 'precio_ml', label: 'Precio ML' },
                { field: 'costo_promedio', label: 'Costo promedio' },
                { field: 'updated_at', label: 'Última edición' },
              ].map((option) => (
                <button
                  key={option.field}
                  onClick={() => {
                    setSortBy(current => ({
                      field: option.field as any,
                      direction: current?.field === option.field && current.direction === 'asc' ? 'desc' : 'asc'
                    }));
                    document.getElementById('sort-menu')?.classList.add('hidden');
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center justify-between"
                >
                  <span>{option.label}</span>
                  {sortBy?.field === option.field && (
                    sortBy.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Replace the "Edición múltiple" button with "Herramientas" */}
          <button
            onClick={() => setShowToolsModal(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mr-2"
          >
            Herramientas
          </button>
          
          {/* Add settings button with gear icon */}
          <button
            onClick={() => setShowCategoryManager(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
          >
            <Settings className="w-4 h-4 mr-1" />
            Categorías
          </button>
        </div>
      </div>

      {/* Add buttons section between search/filters and product list */}
      <div className="flex items-center gap-4 mb-6 mt-4">
        <button
          onClick={handleAddNewProduct}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Añadir producto
        </button>
        <button
          className="px-4 py-2 border border-blue-500 text-blue-500 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Añadir varios productos
        </button>
      </div>

      <div className="space-y-4">
        {filteredProducts.map(product => (
          <div key={product.id} className="space-y-2">
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="mb-4">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-lg font-medium text-gray-900 truncate flex-shrink-0">
                          {product.nombre}
                        </h3>
                        {product.url_pagina_web && (
                          <a
                            href={product.url_pagina_web}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:text-blue-600 flex-shrink-0"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 flex-shrink-0 max-w-[250px] truncate" title={getCategoryPathDisplay(product.categoria_path, categories)}>
                          <Tag className="w-3 h-3 mr-1 flex-shrink-0" />
                          {getCategoryPathDisplay(product.categoria_path, categories)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-8">
                      <div>
                        <div className="text-sm text-gray-500">Precio local</div>
                        <div className="font-medium">{formatCurrency(product.precio_local)}</div>
                        <div className="text-sm text-gray-500">
                          Ganancia: {calculateProfitPercentage(product.precio_local, product.costo_promedio)}
                        </div>
                      </div>
                      {product.precio_web && product.precio_web > 0 && (
                        <div>
                          <div className="text-sm text-gray-500">Precio web</div>
                          <div className="font-medium">{formatCurrency(product.precio_web)}</div>
                        </div>
                      )}
                      {product.precio_ml && product.precio_ml > 0 && (
                        <div>
                          <div className="text-sm text-gray-500">Precio ML</div>
                          <div className="font-medium">{formatCurrency(product.precio_ml)}</div>
                        </div>
                      )}
                      <div>
                        <div className="text-sm text-gray-500">Costo promedio</div>
                        <div className="font-medium">{formatCurrency(product.costo_promedio)}</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <div className="text-xs text-gray-500">
                      Última actualización: {product.updated_at ? formatDate(product.updated_at) : 'N/A'}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(product)}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <PenSquare className="w-4 h-4" />
                        Editar producto
                      </button>
                    </div>
                    <button
                      onClick={() => toggleProduct(product.id)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      {expandedProducts.has(product.id) ? (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {expandedProducts.has(product.id) && product.variants.length > 0 && (
                <div className="border-t bg-gray-50">
                  <div className="p-4">
                    {/* Only show "Variantes" title if there's more than one variant */}
                    {product.variants.length > 1 && (
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Variantes</h4>
                    )}
                    <div className="space-y-3">
                      {product.variants.map(variant => (
                        <div
                          key={variant.id}
                          className="bg-white rounded-lg border border-gray-200 p-4"
                        > 
                          {/* Changed from flex to flex-col when there's only one variant */}
                          <div className="flex flex-col md:flex-row md:items-start gap-4">
                            <div className="flex-1 min-w-0">
                              {/* Only show variant name if there's more than one variant */}
                              {product.variants.length > 1 && (
                                <div className="flex items-center gap-2 mb-2">
                                  <h5 className="font-medium text-gray-900">
                                    {variant.nombre}
                                  </h5>

                                </div>
                              )}
                              <span className="text-sm text-gray-500">
                                SKU: {variant.sku || 'N/A'}
                              </span>

                              {variant.descripcion && (
                                <p className="text-sm text-gray-600 mb-3">
                                  <span className="font-medium">Descripción:</span> {variant.descripcion}
                                </p>
                              )}

                              {/* Only show prices if there's more than one variant */}
                              {product.variants.length > 1 && (
                                <div className="flex flex-wrap gap-6 mb-4 md:mb-0">
                                  <div>
                                    <div className="text-sm text-gray-500">Precio local</div>
                                    <div className="font-medium">{formatCurrency(variant.precio_local)}</div>
                                    <div className="text-sm text-gray-500">
                                      Ganancia: {calculateProfitPercentage(variant.precio_local, variant.costo_promedio)}
                                    </div>
                                  </div>
                                  {variant.precio_web && variant.precio_web > 0 && (
                                    <div>
                                      <div className="text-sm text-gray-500">Precio web</div>
                                      <div className="font-medium">{formatCurrency(variant.precio_web)}</div>
                                    </div>
                                  )}
                                  {variant.precio_ml && variant.precio_ml > 0 && (
                                    <div>
                                      <div className="text-sm text-gray-500">Precio ML</div>
                                      <div className="font-medium">{formatCurrency(variant.precio_ml)}</div>
                                    </div>
                                  )}
                                  <div>
                                    <div className="text-sm text-gray-500">Costo promedio</div>
                                    <div className="font-medium">{formatCurrency(variant.costo_promedio)}</div>
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {variant.atributos && Object.keys(variant.atributos).length > 0 && (
                              <div className="w-full md:w-auto md:min-w-[250px] md:max-w-[350px]">
                                <h6 className="text-sm font-medium text-gray-700 mb-2">Características</h6>
                                <div className="flex flex-wrap gap-2">
                                  {Object.entries(variant.atributos).map(([key, value]) => (
                                    <div key={key} className="bg-gray-50 px-2 py-1 rounded-md">
                                      <span className="font-medium text-gray-600">{key.toString()}: </span>
                                      <span className="text-gray-900">{value.toString()}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {editingProduct && (
        <ProductEditForm
          editingProduct={editingProduct}
          setEditingProduct={setEditingProduct}
          categories={categories}
          onSave={handleSave}
          onCancel={() => setEditingProduct(null)}
          saving={saving}
          showDeleteVariantConfirmation={showDeleteVariantConfirmation}
          setShowDeleteVariantConfirmation={setShowDeleteVariantConfirmation}
          handleDeleteVariant={handleDeleteVariant}
          handleAddVariant={handleAddVariant}
          handleGenerateDescription={handleGenerateDescription}
          generatingDescription={generatingDescription}
          uploadingImages={uploadingImages}
          imageError={imageError}
          handleImageUpload={handleImageUpload}
          handleDeleteImage={handleDeleteImage}
          handleSetPrincipalImage={handleSetPrincipalImage}
          handleReorderImages={handleReorderImages}
          handleDeleteProduct={handleDeleteProduct}
          showDeleteProductConfirmation={showDeleteProductConfirmation}
          setShowDeleteProductConfirmation={setShowDeleteProductConfirmation}
        />
      )}

      {showDeleteProductConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirmar eliminación</h3>
            <p className="text-sm text-gray-600 mb-6">
              ¿Estás seguro de que deseas eliminar este producto y todas sus variantes? Esta acción no se puede deshacer.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDeleteProductConfirmation(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteProduct}
                disabled={saving}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  'Eliminar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteVariantConfirmation !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirmar eliminación</h3>
            <p className="text-sm text-gray-600 mb-6">
              ¿Estás seguro de que deseas eliminar esta variante? Esta acción no se puede deshacer.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDeleteVariantConfirmation(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDeleteVariant(showDeleteVariantConfirmation)}
                disabled={saving}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  'Eliminar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showToolsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-6">Herramientas</h3>
            
            <div className="space-y-4">
              <div
                className="p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => {
                  setShowToolsModal(false);
                  setShowBulkEditForm(true);
                }}
              >
                <h4 className="text-sm font-medium text-gray-900">Editar precios de varios productos</h4>
                <p className="text-sm text-gray-600">Modifica los precios de una o varias categorías de productos de forma porcentual</p>
              </div>

              <div
                className="p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => {
                  setShowToolsModal(false);
                  setShowGenerateDescriptionsConfirmation(true);
                }}
              >
                <h4 className="text-sm font-medium text-gray-900">Completar descripciones de productos</h4>
                <p className="text-sm text-gray-600">Genera usando IA una descripción para todos los productos que actualmente no tengan una</p>
              </div>

              <div
                className="p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => {
                  setShowToolsModal(false);
                  setShowClassifyProductsConfirmation(true);
                }}
              >
                <h4 className="text-sm font-medium text-gray-900">Clasificar productos por categoría</h4>
                <p className="text-sm text-gray-600">Clasifica por categoría todos los productos que actualmente no lo estén. De ser necesario, se crearán nuevas categorías para estos.</p>
              </div>

              <div
                className="p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => {
                  setShowToolsModal(false);
                  setShowCategoryManager(true);
                }}
              >
                <h4 className="text-sm font-medium text-gray-900">Gestionar categorías</h4>
                <p className="text-sm text-gray-600">Edita, elimina o crea nuevas categorías</p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => setShowToolsModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {showGenerateDescriptionsConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirmar generación de descripciones</h3>
            <p className="text-sm text-gray-600 mb-6">
              ¿Estás seguro de que deseas generar descripciones para todos los productos que no tienen una?
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowGenerateDescriptionsConfirmation(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleGenerateAllDescriptions}
                disabled={generatingDescriptions}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {generatingDescriptions ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generando...
                  </>
                ) : (
                  'Confirmar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showClassifyProductsConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirmar clasificación de productos</h3>
            <p className="text-sm text-gray-600 mb-6">
              ¿Estás seguro de que deseas clasificar todos los productos que actualmente no lo estén? Esta acción no se puede deshacer.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowClassifyProductsConfirmation(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleClassifyProducts}
                disabled={classifyingProducts}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {classifyingProducts ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generando...
                  </>
                ) : (
                  'Confirmar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkEditForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-6">Edición múltiple</h3>
            
            {/* AI Suggestion Card */}
            <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-blue-600" />
                <h4 className="text-sm font-medium text-blue-800">Sugerencia de la IA</h4>
              </div>
              <div className="text-sm text-blue-700">
                {loadingSuggestion ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generando...
                  </div>
                ) : (
                  aiSuggestion || "No hay sugerencias disponibles"
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
                <div className="relative">
                  <div 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent flex justify-between items-center cursor-pointer"
                    onClick={() => setShowBulkEditCategoryMenu(!showBulkEditCategoryMenu)}
                  >
                    <span>
                      {bulkEditForm.categoria_id ? 
                        categories.find(cat => cat.id === bulkEditForm.categoria_id)?.nombre || 'Seleccionar categoría' : 
                        'Seleccionar categoría'}
                    </span>
                    <ChevronDown size={16} />
                  </div>
                  
                  {/* Show the CategoryHoverMenu when toggled */}
                  {showBulkEditCategoryMenu && (
                    <div className="absolute z-50 mt-1 w-full">
                      <CategoryHoverMenu
                        categories={categories}
                        categoriesMap={categoriesMap}
                        onSelectCategory={(id) => {
                          setBulkEditForm(prev => ({
                            ...prev,
                            categoria_id: id || null
                          }));
                          setShowBulkEditCategoryMenu(false);
                        }}
                        selectedCategory={bulkEditForm.categoria_id}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Precio a modificar</label>
                <select
                  name="precioType"
                  value={bulkEditForm.precioType}
                  onChange={handleBulkEditFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="local">Precio local</option>
                  <option value="web">Precio web</option>
                  <option value="ml">Precio ML</option>
                  <option value="both">Todos los precios</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Operación</label>
                <div className="relative">
                  <select
                    name="operation"
                    value={bulkEditForm.operation}
                    onChange={handleBulkEditFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                  >
                    <option value="increase">Aumentar</option>
                    <option value="decrease">Disminuir</option>
                  </select>
                  <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                    {bulkEditForm.operation === 'increase' ? (
                      <Plus className="w-4 h-4 text-gray-500" />
                    ) : (
                      <Minus className="w-4 h-4 text-gray-500" />
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Porcentaje</label>
                <div className="relative">
                  <input
                    type="number"
                    name="percentage"
                    value={bulkEditForm.percentage}
                    onChange={handleBulkEditFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                  />
                  <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                    <span className="text-gray-500">%</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Redondear nuevos precios a la centena más cercana</label>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="roundToNearestHundred"
                    checked={bulkEditForm.roundToNearestHundred}
                    onChange={handleBulkEditFormChange}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-600">
                    Los precios que sean modificados se redondearán a la centena más cercana. Ejemplo: Nuevo precio: $2462, Nuevo precio redondeado: $2500.
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => setShowBulkEditForm(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => setShowBulkEditConfirmation(true)}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Aplicar cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkEditConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirmar edición múltiple</h3>
            <p className="text-sm text-gray-600 mb-6">
              ¿Estás seguro de que deseas aplicar estos cambios a todos los productos seleccionados?
              {bulkEditForm.roundToNearestHundred && (
                <span className="block mt-2 text-gray-500">
                  Los precios se redondearán a la centena más cercana.
                </span>
              )}
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowBulkEditConfirmation(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleBulkEditSubmit}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showCategoryManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div 
            ref={categoryModalRef}
            className="bg-white rounded-md shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-0">
            <div className="sticky top-0 bg-white z-10 border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Gestión de categorías</h3>
              <button 
                onClick={() => setShowCategoryManager(false)} 
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 text-red-800 p-3 rounded-md flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                <span>{error}</span>
                <button 
                  className="ml-auto text-red-600 hover:text-red-800" 
                  onClick={() => setError(null)}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {categoryAction ? (
              <div className="bg-gray-50 p-6 rounded-md mb-6 border border-gray-200 shadow-sm">
                <h4 className="text-md font-semibold text-gray-900 mb-4">
                  {categoryAction === 'add' ? 'Añadir nueva categoría' : 'Editar categoría'}
                </h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre de la categoría
                    </label>
                    <input
                      id="category-name"
                      type="text"
                      value={editingCategory?.nombre || ''}
                      onChange={(e) => setEditingCategory(prev => prev ? { ...prev, nombre: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      placeholder="Ingrese el nombre de la categoría"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Categoría padre
                    </label>
                    <select
                      id="parent-category"
                      value={editingCategory?.parent_id || ''}
                      onChange={(e) => {
                        const parentId = e.target.value ? parseInt(e.target.value, 10) : null;
                        setEditingCategory(prev => prev ? { ...prev, parent_id: parentId } : null);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <option value="">Sin categoría padre</option>
                      {categories.map(cat => (
                        // Prevent a category from being its own parent or child of a category that would create a circular reference
                        editingCategory?.id !== cat.id && 
                        (!editingCategory?.id || !cat.path.includes(`/${editingCategory.id}/`)) && (
                          <option key={cat.id} value={cat.id}>
                            {getCategoryPathDisplay(cat.path, categories)}
                          </option>
                        )
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={handleCancelCategoryEdit}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-normal"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveCategory}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-normal"
                  >
                    {categoryAction === 'add' ? 'Añadir' : 'Guardar cambios'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mb-6 mt-4 flex justify-center">
                <button
                  onClick={() => handleAddCategory(null)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center font-normal"
                >
                  <Plus className="w-4 h-4 mr-1" /> Añadir categoría
                </button>
              </div>
            )}

            <div className="bg-white rounded-md border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-blue-50 text-sm font-medium text-gray-700 flex items-center justify-between">
                <span>Categorías</span>
                <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full border border-gray-200">
                  {categories.length} {categories.length === 1 ? 'categoría' : 'categorías'}
                </span>
              </div>
              
              <div className="p-4 bg-gray-50">
                {categories.length === 0 ? (
                  <div className="text-center py-12 px-4 text-gray-500 bg-white rounded-md border border-dashed border-gray-300 flex flex-col items-center">
                    <div className="mb-3 bg-gray-100 rounded-full p-3">
                      <Tag className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-gray-600 font-medium">No hay categorías disponibles</p>
                    <p className="text-sm text-gray-500 mt-1">Crea tu primera categoría con el botón "Añadir categoría"</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Root level categories */}
                    {categories
                      .filter(cat => cat.path.split('/').filter(Boolean).length === 1)
                      .sort((a, b) => a.nombre.localeCompare(b.nombre))
                      .map(category => (
                        <CategoryTreeItem
                          key={category.id}
                          category={category}
                          categories={categories}
                          level={0}
                          expandedCategoryIds={expandedCategoryIds}
                          onToggleExpand={handleToggleCategoryExpansion}
                          onEdit={handleEditCategory}
                          onDelete={handleDeleteCategory}
                          onAddSubcategory={handleAddCategory}
                          deletingCategoryId={deletingCategoryId}
                        />
                      ))
                    }
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Category Menu Item Component
interface CategoryMenuItemProps {
  category: Category;
  categoriesMap: Map<number, Category[]>;
  level: number;
  selectedCategory: number | null;
  onSelectCategory: (id: number | null) => void;
  openSubmenus: Set<number>;
  toggleSubmenu: (id: number, isOpen: boolean) => void;
}

const CategoryMenuItem: React.FC<CategoryMenuItemProps> = ({
  category,
  categoriesMap,
  level,
  selectedCategory,
  onSelectCategory,
  openSubmenus,
  toggleSubmenu
}) => {
  const hasChildren = categoriesMap.has(category.id);
  const isOpen = openSubmenus.has(category.id);
  const isSelected = selectedCategory === category.id;
  
  // Determine padding class based on level
  let paddingClass = 'px-4';
  if (level === 1) paddingClass = 'pl-8 pr-4';
  else if (level === 2) paddingClass = 'pl-12 pr-4';
  else if (level === 3) paddingClass = 'pl-16 pr-4';
  else if (level >= 4) paddingClass = 'pl-20 pr-4';
  
  return (
    <div className="w-full">
      <div
        className={`flex items-center py-2 ${paddingClass} hover:bg-gray-50 ${isSelected ? 'bg-blue-100 text-blue-800' : ''}`}
      >
        <div className="flex-1 cursor-pointer"
          onClick={() => onSelectCategory(category.id)}
        >
          {category.nombre}
        </div>
        {hasChildren && (
          <button
            type="button"
            className="p-1 rounded-full hover:bg-gray-200"
            onClick={() => toggleSubmenu(category.id, !isOpen)}
          >
            {isOpen ? 
              <ChevronUp className="w-4 h-4" /> : 
              <ChevronDown className="w-4 h-4" />
            }
          </button>
        )}
      </div>
      
      {hasChildren && isOpen && (
        <div>
          {categoriesMap.get(category.id)!.map((childCategory) => (
            <CategoryMenuItem
              key={childCategory.id}
              category={childCategory}
              categoriesMap={categoriesMap}
              level={level + 1}
              selectedCategory={selectedCategory}
              onSelectCategory={onSelectCategory}
              openSubmenus={openSubmenus}
              toggleSubmenu={toggleSubmenu}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// CategoryHoverMenu component for a hover-based multi-column dropdown
const CategoryHoverMenu: React.FC<CategoryHoverMenuProps> = ({
  categories,
  categoriesMap,
  onSelectCategory,
  selectedCategory
}) => {
  // State to track which category columns are currently visible
  const [hoveredCategories, setHoveredCategories] = useState<number[]>([]);
  
  // Function to handle hovering over a category
  const handleCategoryHover = (categoryId: number | null, columnIndex: number) => {
    if (categoryId === null) {
      // If hovering away, remove all columns after the current index
      setHoveredCategories(prev => prev.slice(0, columnIndex));
    } else {
      // If hovering on a category, update columns after this index
      setHoveredCategories(prev => {
        const newHovered = [...prev.slice(0, columnIndex)];
        newHovered.push(categoryId);
        return newHovered;
      });
    }
  };
  
  // Root categories - those without parents
  const rootCats = useMemo(() => {
    return categories.filter(category => {
      const segments = category.path.split('/').filter(Boolean);
      return segments.length === 1;
    });
  }, [categories]);
  
  return (
    <div className="relative" style={{ height: '300px' }}>
      {/* First column with root categories */}
      <div className="absolute left-0 top-0 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-30 overflow-hidden">
        <div className="p-1">
          <button
            onClick={() => onSelectCategory(null)}
            className="w-full px-4 py-2 text-left hover:bg-gray-50 text-gray-800 rounded"
          >
            Todas las categorías
          </button>
          
          <CategoryColumn
            categories={rootCats}
            categoriesMap={categoriesMap}
            onSelectCategory={onSelectCategory}
            onHover={(id) => handleCategoryHover(id, 0)}
            selectedCategory={selectedCategory}
          />
        </div>
      </div>
      
      {/* Render additional columns for each hovered category */}
      {hoveredCategories.map((categoryId, index) => {
        // Get the child categories for this hovered category
        const childCategories = categoriesMap.get(categoryId) || [];
        
        if (childCategories.length === 0) return null;
        
        return (
          <div 
            key={categoryId}
            className="absolute top-0 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-30 overflow-hidden"
            style={{ left: `${(index + 1) * 16}rem` }}
          >
            <div className="p-1">
              <CategoryColumn
                categories={childCategories}
                categoriesMap={categoriesMap}
                onSelectCategory={onSelectCategory}
                onHover={(id) => handleCategoryHover(id, index + 1)}
                selectedCategory={selectedCategory}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Column of category items
const CategoryColumn: React.FC<CategoryColumnProps> = ({
  categories,
  categoriesMap,
  onSelectCategory,
  onHover,
  selectedCategory
}) => {
  return (
    <div className="max-h-80 overflow-y-auto">
      {categories.map(category => (
        <CategoryItem
          key={category.id}
          category={category}
          hasChildren={categoriesMap.has(category.id)}
          isSelected={selectedCategory === category.id}
          onSelect={() => {
            onSelectCategory(category.id);
            document.getElementById('category-menu')?.classList.add('hidden');
          }}
          onHover={() => onHover(category.id)}
        />
      ))}
    </div>
  );
};

// Individual category item
const CategoryItem: React.FC<CategoryItemProps> = ({
  category,
  hasChildren,
  isSelected,
  onSelect,
  onHover
}) => {
  return (
    <div
      className={`flex items-center px-4 py-2 hover:bg-gray-100 transition-colors ${isSelected ? 'bg-blue-100 text-blue-800' : 'text-gray-800'}`}
      onMouseEnter={onHover}
    >
      <div 
        className="flex-1 cursor-pointer"
        onClick={onSelect}
      >
        {category.nombre}
      </div>
      {hasChildren && (
        <div className="flex items-center justify-center w-6">
          <ChevronRight className="w-4 h-4 text-gray-500" />
        </div>
      )}
    </div>
  );
};

// Category Tree Item
interface CategoryTreeItemProps {
  category: Category;
  categories: Category[];
  level: number;
  expandedCategoryIds: Set<number>;
  onToggleExpand: (categoryId: number) => void;
  onEdit: (category: Category) => void;
  onDelete: (categoryId: number) => void;
  onAddSubcategory: (parentId: number | null) => void;
  deletingCategoryId: number | null;
}

const CategoryTreeItem: React.FC<CategoryTreeItemProps> = ({
  category,
  categories,
  level,
  expandedCategoryIds,
  onToggleExpand,
  onEdit,
  onDelete,
  onAddSubcategory,
  deletingCategoryId
}) => {
  const hasChildren = categories.some(cat => 
    cat.path.startsWith(category.path) && cat.id !== category.id
  );
  const isExpanded = expandedCategoryIds.has(category.id);
  const isDeleting = deletingCategoryId === category.id;

  // Get child count
  const childCount = categories.filter(cat => 
    cat.path.startsWith(category.path) && cat.id !== category.id
  ).length;

  // Determine margin class based on level
  let marginClass = '';
  if (level === 1) marginClass = 'ml-4';
  else if (level === 2) marginClass = 'ml-8';
  else if (level === 3) marginClass = 'ml-12';
  else if (level >= 4) marginClass = 'ml-16';

  return (
    <div className="flex flex-col mb-3">
      <div
        className={`bg-white border border-gray-200 rounded-md shadow-sm hover:shadow transition-all duration-200 overflow-hidden ${marginClass}`}
      >
        <div className="flex justify-between items-center p-3">
          <div className="flex items-center gap-2 flex-1">
            {hasChildren && (
              <button
                type="button"
                className="p-1 rounded-md hover:bg-gray-100 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                onClick={() => onToggleExpand(category.id)}
              >
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4 text-blue-500" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-blue-500" />
                )}
              </button>
            )}
            <div>
              <span className="text-gray-800 font-medium">{category.nombre}</span>
              {childCount > 0 && (
                <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full">
                  {childCount} {childCount === 1 ? 'subcategoría' : 'subcategorías'}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="p-1.5 rounded-md hover:bg-blue-50 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              onClick={() => onEdit(category)}
              title="Editar categoría"
            >
              <PenSquare className="w-4 h-4 text-blue-500" />
            </button>
            <button
              type="button"
              className="p-1.5 rounded-md hover:bg-red-50 focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
              onClick={() => onDelete(category.id)}
              disabled={isDeleting}
              title="Eliminar categoría"
            >
              {isDeleting ? (
                <Loader2 className="w-4 h-4 animate-spin text-red-600" />
              ) : (
                <Trash className="w-4 h-4 text-red-600" />
              )}
            </button>
            <button
              type="button"
              className="p-1.5 rounded-md hover:bg-blue-50 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              onClick={() => onAddSubcategory(category.id)}
              title="Añadir subcategoría"
            >
              <Plus className="w-4 h-4 text-blue-500" />
            </button>
          </div>
        </div>
        <div className="px-3 pb-2 text-xs text-gray-500 border-t border-gray-100 pt-1 bg-gray-50 flex justify-between">
          <span>{getCategoryPathDisplay(category.path, categories)}</span>
        </div>
      </div>
      {hasChildren && isExpanded && (
        <div className="mt-2">
          {categories
            .filter(cat => cat.path.startsWith(category.path) && cat.id !== category.id && cat.path.split('/').filter(Boolean).length === category.path.split('/').filter(Boolean).length + 1)
            .sort((a, b) => a.nombre.localeCompare(b.nombre))
            .map(subcategory => (
              <CategoryTreeItem
                key={subcategory.id}
                category={subcategory}
                categories={categories}
                level={level + 1}
                expandedCategoryIds={expandedCategoryIds}
                onToggleExpand={onToggleExpand}
                onEdit={onEdit}
                onDelete={onDelete}
                onAddSubcategory={onAddSubcategory}
                deletingCategoryId={deletingCategoryId}
              />
            ))}
        </div>
      )}
    </div>
  );
};